import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { addComma } from '../../../lib/common';
import { BLACK, GRAY, RED } from '../../../styles/constants/color';
import { fontFrame } from '../../../styles/mixin/font';

const Input = styled.input<{
  inputWidth: string;
  inputHeight: string;
  fontWeight: string;
  fontSize: string;
  lineHeight: string;
  color: string;
  inputTextAlign: string;
  alertBorder: boolean;
}>`
  width: ${props => props.inputWidth};
  height: ${props => props.inputHeight};
  border: 1px solid ${props => (props.alertBorder ? RED : GRAY)};
  ${props => fontFrame(props.fontWeight, props.fontSize, props.lineHeight, props.color)};
  text-align: ${props => props.inputTextAlign};
  padding: 0 4px;
`;

interface Props {
  width?: string;
  height?: string;
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value?: number;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  textAlign?: string;
  needZero?: boolean;
  notComma?: boolean;
  disabled?: boolean;
  alertBorder?: boolean;
}

function NumberInput({
  width = '65px',
  height = '25px',
  name,
  placeholder = '',
  onChange,
  onBlur,
  value = 0,
  fontWeight = '400',
  fontSize = '12px',
  lineHeight = '1',
  color = BLACK,
  textAlign = 'right',
  needZero = false,
  notComma = false,
  disabled = false,
  alertBorder = false,
}: Props): JSX.Element {
  const [toggle, setToggle] = useState<boolean>(false);
  const [input, setInput] = useState<number>(0);
  const fetchValue: number = onChange ? value : input;

  useEffect(() => {
    setInput(value);
  }, [value]);
  return (
    <>
      {toggle ? (
        <Input
          type='number'
          inputWidth={width}
          inputHeight={height}
          inputTextAlign={textAlign}
          name={name}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange ? onChange(e) : setInput(Number(e.target.value));
          }}
          onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
            setToggle(false);
            onBlur && onBlur(e);
          }}
          value={fetchValue || ''}
          fontWeight={fontWeight}
          fontSize={fontSize}
          lineHeight={lineHeight}
          color={color}
          inputMode='numeric'
          autoFocus
          disabled={disabled}
          alertBorder={alertBorder}
        />
      ) : (
        <Input
          type='text'
          placeholder={placeholder}
          inputWidth={width}
          inputHeight={height}
          inputTextAlign={textAlign}
          onClick={() => {
            setToggle(true);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange ? onChange(e) : setInput(Number(e.target.value));
          }}
          onFocus={() => setToggle(true)}
          value={notComma ? fetchValue || (needZero ? 0 : '') : addComma(fetchValue, needZero)}
          fontWeight={fontWeight}
          fontSize={fontSize}
          lineHeight={lineHeight}
          color={color}
          disabled={disabled}
          alertBorder={alertBorder}
        />
      )}
    </>
  );
}

/**
 *  기존에는 세자리마다 ,가 붙은 형태의 가격을 보여주면서 클릭해서 수정할 땐 ,를 제거하고 숫자로만 input을 제어하는 컴포넌트
 * @param {string} width input의 width (65px)
 * @param {string} height input의 height (25px)
 * @param {string} name input의 name
 * @param {string} placeholder input의 placeholder
 * @param {Function} onChange onChange 이벤트
 * @param {Function} onBlur onBlur 이벤트
 * @param {number} value input의 value (default: 0)
 * @param {string} fontWeight input의 fontWeight '400', Medium, Bold (default: '500')
 * @param {string} fontSize input의 fontSize (defalut: 13px)
 * @param {string} lineHeight input의 lineHeight (default: 1)
 * @param {string} color input의 color (default: #222)
 * @param {boolean} needZero 숫자 0 표현 여부 (default: false)
 * @param {boolean} notComma , 불필요 여부 (default: false)
 * @param {boolean} disabled disabled 여부 (default: false)
 * @param {boolean} alertBorder 빨간색 경고안내성 border 여부 (default: false)
 *  */
export default React.memo(NumberInput);
